import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isEmpty, omit } from 'lodash';
import { useRef } from 'react';
import { FormSections, Input, Select, useLocalizeMessage } from 'libs.nucleus';
import { useCountriesOptions } from 'libs.react';
import { buildTimezoneOptions, validateFormFieldOnBlur, validateFormFieldOnChange, } from './workspace_site_form.helpers';
import { useSearchWorkspaceSiteByName } from '../../hooks/use_search_workspace_site_by_name';
import { NoPIIToggle } from '../no_pii_toggle';
export const WorkspaceSiteForm = ({ formValue, formErrors, onChange, onError, workspaceSite, }) => {
    const translate = useLocalizeMessage();
    const { checkForDuplicate } = useSearchWorkspaceSiteByName();
    const duplicateCheckSequence = useRef(0);
    const countriesOptions = useCountriesOptions();
    const timezoneOptions = buildTimezoneOptions();
    const handleInputFieldChange = (event) => {
        const { name, value } = event.target;
        const error = validateFormFieldOnChange(name, value);
        onChange((prevState) => ({ ...prevState, [name]: value }));
        onError((prevState) => (error ? { ...prevState, [name]: translate(error) } : omit(prevState, name)));
    };
    const handleNameFieldChange = (event) => {
        const { value } = event.target;
        const error = validateFormFieldOnChange('name', value);
        onChange((prevState) => ({ ...prevState, name: value }));
        // Always clear the name error on change (clears stale duplicate errors from blur)
        onError((prevState) => (error ? { ...prevState, name: translate(error) } : omit(prevState, 'name')));
    };
    const handleNameFieldBlur = async (event) => {
        const { value } = event.target;
        const trimmedValue = value.trim();
        const error = validateFormFieldOnBlur('name', trimmedValue);
        // Handle basic validation error first
        if (error !== formErrors.name) {
            onError((prevState) => (error ? { ...prevState, name: translate(error) } : omit(prevState, 'name')));
        }
        if (trimmedValue !== formValue.name) {
            onChange((prevState) => ({ ...prevState, name: trimmedValue }));
        }
        // Skip duplicate check if name is empty or has validation error
        if (!trimmedValue || error) {
            return;
        }
        // Skip duplicate check in edit mode if name hasn't changed
        if (workspaceSite && trimmedValue === workspaceSite.name) {
            // Clear any previous duplicate error
            onError((prevState) => omit(prevState, 'name'));
            return;
        }
        // Increment sequence to track this request
        duplicateCheckSequence.current += 1;
        const currentSequence = duplicateCheckSequence.current;
        // Check for duplicate name
        const hasDuplicate = await checkForDuplicate(trimmedValue, workspaceSite?.id);
        // Ignore stale responses from earlier requests
        if (currentSequence !== duplicateCheckSequence.current) {
            return;
        }
        // Update error state based on duplicate check result
        if (hasDuplicate) {
            onError((prevState) => ({ ...prevState, name: translate('Site name already exists') }));
        }
        else {
            // Clear any previous duplicate error (only if no sync validation error exists)
            if (!error) {
                onError((prevState) => omit(prevState, 'name'));
            }
        }
    };
    const handleInputFieldBlur = (event) => {
        const { name, value } = event.target;
        const trimmedValues = value.trim();
        const error = validateFormFieldOnBlur(name, trimmedValues);
        if (error !== formErrors[name]) {
            onError((prevState) => (error ? { ...prevState, [name]: translate(error) } : omit(prevState, name)));
        }
        if (trimmedValues !== formValue[name]) {
            onChange((prevState) => ({ ...prevState, [name]: trimmedValues }));
        }
    };
    const handleSelectFieldChange = (name, value) => {
        const error = validateFormFieldOnChange(name, value.value);
        onChange((prevState) => ({ ...prevState, [name]: value.value }));
        onError((prevState) => (error ? { ...prevState, [name]: translate(error) } : omit(prevState, name)));
    };
    const getSelectValue = (options, value) => {
        return isEmpty(value) ? { label: '', value: '' } : options.find((option) => option.value === value);
    };
    const handleToggleChange = (checked) => {
        onChange((prevState) => ({ ...prevState, noPII: checked }));
    };
    const getDetailsSectionContent = () => {
        return (_jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx(NoPIIToggle, { checked: formValue.noPII, onChange: handleToggleChange }), _jsx(Input, { id: 'name', dataTestId: 'name', name: 'name', label: translate('Name'), width: 'lg', required: true, hasError: !!formErrors.name, errorMessage: formErrors.name, value: formValue.name, onChange: handleNameFieldChange, onBlur: handleNameFieldBlur }), _jsx(Input, { id: 'number', dataTestId: 'number', name: 'number', label: translate('Workspace site ID'), width: 'lg', hasError: !!formErrors.number, errorMessage: formErrors.number, value: formValue.number, onChange: handleInputFieldChange, onBlur: handleInputFieldBlur }), _jsx(Select, { dataTestId: 'timezone', label: translate('Timezone'), options: timezoneOptions, width: 'lg', hasError: !!formErrors.timezone, errorMessage: formErrors.timezone, value: getSelectValue(timezoneOptions, formValue.timezone), onChange: (value) => handleSelectFieldChange('timezone', value) })] }));
    };
    const getContactInformationSectionContent = () => {
        return (_jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx(Input, { id: 'email', dataTestId: 'email', name: 'email', label: translate('Email'), width: 'lg', hasError: !!formErrors.email, errorMessage: formErrors.email, value: formValue.email, onChange: handleInputFieldChange, onBlur: handleInputFieldBlur }), _jsx(Input, { required: true, id: 'phone', dataTestId: 'phone', name: 'phone', type: 'tel', label: translate('Phone'), width: 'lg', hasError: !!formErrors.phone, errorMessage: formErrors.phone, value: formValue.phone, onChange: handleInputFieldChange, onBlur: handleInputFieldBlur }), _jsx(Select, { required: true, dataTestId: 'country-code', label: translate('Country'), width: 'lg', hasError: !!formErrors.countryCode, errorMessage: formErrors.countryCode, options: countriesOptions, value: getSelectValue(countriesOptions, formValue.countryCode), onChange: (value) => handleSelectFieldChange('countryCode', value) }), _jsx(Input, { required: true, id: 'addressLine', dataTestId: 'address-line', name: 'addressLine', label: translate('Address'), width: 'lg', hasError: !!formErrors.addressLine, errorMessage: formErrors.addressLine, value: formValue.addressLine, onChange: handleInputFieldChange, onBlur: handleInputFieldBlur }), _jsx(Input, { required: true, id: 'city', dataTestId: 'city', name: 'city', label: translate('City / Town'), width: 'lg', hasError: !!formErrors.city, errorMessage: formErrors.city, value: formValue.city, onChange: handleInputFieldChange, onBlur: handleInputFieldBlur }), _jsx(Input, { required: true, id: 'region', dataTestId: 'region', name: 'region', label: translate('State / Province / Region'), width: 'lg', hasError: !!formErrors.region, errorMessage: formErrors.region, value: formValue.region, onChange: handleInputFieldChange, onBlur: handleInputFieldBlur }), _jsx(Input, { id: 'postalCode', dataTestId: 'postal-code', name: 'postalCode', label: translate('Zip / Postal code'), width: 'lg', hasError: !!formErrors.postalCode, errorMessage: formErrors.postalCode, value: formValue.postalCode, onChange: handleInputFieldChange, onBlur: handleInputFieldBlur })] }));
    };
    const formSections = [
        {
            title: translate('Details'),
            content: getDetailsSectionContent(),
        },
        {
            title: translate('Contact information'),
            content: getContactInformationSectionContent(),
        },
    ];
    return (_jsx("form", { autoComplete: 'off', children: _jsx(FormSections, { sections: formSections }) }));
};
